/** Help text for combination list tables in component tabs. */

export const COMBO_QUANTITY_COL =
    "Number of receipts matching this row";

export const SHARE_PCT_COL =
    "Share of matching receipts in scope — exact combinations are specific, so even the top row may be a small percentage";

export function genericListTooltip(component: string): string {
    const label = component.toLowerCase();
    return (
        `Groups purchase combinations by which ${label} types appear together. ` +
        `Item counts (1× vs 2×) are collapsed into one row. ` +
        `Click a row to filter the exact item-count list. ` +
        `The Receipts column counts matching receipts.`
    );
}

export function withQtyListTooltip(component: string): string {
    const label = component.toLowerCase();
    return (
        `Groups purchase combinations by exact ${label} types and item counts ` +
        `(e.g. 1× vs 2× of the same item are separate rows). ` +
        `Filtered when you select a row in the any-item-count list. ` +
        `The Receipts column counts matching receipts.`
    );
}

export function genericItemColTooltip(component: string): string {
    return `${component} types in the combination (item counts not shown)`;
}

export function withQtyItemColTooltip(component: string): string {
    return `Exact ${component.toLowerCase()} types and item counts in the combination`;
}

export function genericPctColTooltip(): string {
    return "Share of receipts with this item type mix";
}

export function withQtyPctColTooltip(): string {
    return "Share of receipts with this exact type+count pattern";
}

function productTypeExamples(component: string): { mix: string; types: string } {
    const label = component.toLowerCase();
    if (label.includes("loose interior")) {
        return { mix: "drawer mat + box", types: "drawer mat, box, divider" };
    }
    if (label.includes("fixed interior")) {
        return { mix: "drawer + shelf", types: "drawer, shelf, rail" };
    }
    if (label.includes("knob") || label.includes("handle")) {
        return { mix: "knob + handle", types: "knob, handle" };
    }
    if (label.includes("leg")) {
        return { mix: "different leg types", types: "leg" };
    }
    if (label.includes("top panel")) {
        return { mix: "different top panel types", types: "top panel" };
    }
    return { mix: "multiple product types", types: "product types in this category" };
}

export function productTypeListTooltip(component: string): string {
    if (/light|top panel/i.test(component)) {
        return (
            `${productTypeChartTooltip(component)} ` +
            `Click a row to filter. The Receipts column counts matching receipts.`
        );
    }
    const label = component.toLowerCase();
    const { mix } = productTypeExamples(component);
    return (
        `Groups purchase combinations by which ${label} product types appear together ` +
        `(e.g. ${mix}). Click a row to filter. ` +
        `The Receipts column counts matching receipts.`
    );
}

export function productTypeChartTooltip(component: string): string {
    if (component.toLowerCase().includes("top panel")) {
        return (
            "Groups combinations by their distinct top panel product types. " +
            "Mixed means more than one product type appears in the combination. " +
            "Each combination contributes its receipt count once."
        );
    }
    if (component.toLowerCase().includes("light")) {
        return (
            "Groups combinations by their distinct lighting product types. " +
            "Each combination contributes its receipt count once to its product-type group; " +
            "multiple types share a combined label."
        );
    }
    const { mix } = productTypeExamples(component);
    return `Groups purchase combinations by which ${component.toLowerCase()} product types appear together (e.g. ${mix}).`;
}

export function productTypeCombinationColTooltip(component: string): string {
    if (component.toLowerCase().includes("light")) {
        return "Lighting product types in the combination";
    }
    const { types } = productTypeExamples(component);
    return `Product types in the combination (e.g. ${types})`;
}

export function productTypePctColTooltip(): string {
    return "Share of receipts with this product-type mix";
}
